import React from "react";
import { Helmet } from "react-helmet";
import Joi from "joi-browser";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { removeHttpPrefix } from "@shared/lib/utils";

import { organizationRemotenessOptions } from "@ats/src/lib/lookups";
import { hudDisplayVisibilityOptions } from "@ats/src/lib/newLookups";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import FormSection from "@ats/src/components/forms/FormSection";
import FormInput from "@ats/src/components/forms/FormInput";
import FormSelect from "@ats/src/components/forms/FormSelect";
import Button from "@ats/src/components/shared/Button";

import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";

import { useUpdateOrganization } from "@shared/queryHooks/useOrganization";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useCreateOrganizationDataExport } from "@shared/queryHooks/useOrganizationDataExport";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

function AccountOrganizationDetails(props) {
  const addToast = useToastContext();
  const { currentOrganization } = useCurrentSession();
  const [state, setState] = React.useState(currentOrganization);
  const [errors, setErrors] = React.useState(null);
  window.logger("%c[AccountOrganizationDetails] render state", "color: #1976D2", {
    currentOrganization,
    state,
  });

  const {
    mutate: updateOrganization,
    isLoading: isLoadingUpdateOrganization,
  } = useUpdateOrganization();

  const {
    mutate: createDataExport,
    isLoading: isCreatingDataExport,
  } = useCreateOrganizationDataExport();

  React.useEffect(() => {
    if (currentOrganization != undefined) {
      setState(currentOrganization);
    }
  }, [currentOrganization]);

  if (currentOrganization == undefined || state == undefined) {
    return <LoadingIndicator label="Loading..." />;
  }

  const { name, websiteUrl, remoteness, hudDisplayVisibility } = state;

  const onClickSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    const schema = Joi.object().keys({
      name: Joi.string().min(2).required(),
      websiteUrl: Joi.string(),
      // /\A[a-z0-9_-.]+\z/i
    });

    Joi.validate({ name }, schema, (err) => {
      let errors;
      if (err !== null) {
        errors = {};

        err.details.forEach((index) => {
          let key = index.context.key;
          let message = index.message.replace(`"${key}" `, "");
          errors[key] = [message];
        });

        window.logger("%c[OrganizationForm] handleSubmit errors", "color: #1976D2", err.details);
      } else {
        updateOrganization(
          {
            id: currentOrganization.id,
            name,
            websiteUrl,
            remoteness,
            hudDisplayVisibility,
          },
          {
            onSuccess: () => {
              props.setIsDirty(false);
              addToast({ title: `Successfully updated organization`, kind: "success" });
              setErrors(null);
            },
            onError: (response: any) => {
              addToast({
                title: `Failed to update organization`,
                kind: "danger",
              });
              setErrors(response.data.errors);
            },
          },
        );
      }

      setErrors(errors);
    });
  };

  const onFormInputChange = (name, value) => {
    // window.logger("%c[AccountOrganizationDetails] onFormInputChange", "color: #1976D2", { name, value });
    props.setIsDirty(true);
    setState({ ...state, [name]: value });
  };

  const includeNoRemoteInRemotenessOptions = () => {
    return [
      ...organizationRemotenessOptions,
      {
        value: "remoteness_none",
        label: "No remote",
      },
    ];
  };

  const expandedOrganizationRemotenessOptions = includeNoRemoteInRemotenessOptions();

  const handleExportClick = (kind: "candidates" | "jobs" | "job_applications" | "complete") => {
    return (e) => {
      e.preventDefault();
      createDataExport(
        { organizationId: currentOrganization.id, kind },
        {
          onSuccess: (response: any) => {
            window.logger(
              "%c[AccountOrganizationDetails] handleExportClick onSuccess",
              "color: #1976D2",
              { response },
            );
            addToast({
              title: "Export processing. You will receive an email when it is ready.",
              kind: "success",
            });
          },
          onError: (response: any) => {
            window.logger(
              "%c[AccountOrganizationDetails] handleExportClick onError",
              "color: #1976D2",
              { response },
            );
            addToast({ title: "Failed to start export", kind: "danger" });
          },
        },
      );
    };
  };

  const onClickExportCandidates = handleExportClick("candidates");
  const onClickExportJobs = handleExportClick("jobs");
  const onClickExportJobApplications = handleExportClick("job_applications");
  const onClickExportComplete = handleExportClick("complete");

  const bottomBarContent = (
    <Button
      className="submit-button"
      styleType="primary"
      onClick={onClickSubmit}
      loading={isLoadingUpdateOrganization}
    >
      Save changes
    </Button>
  );

  return (
    <>
      <Helmet title="Organization" />
      <SettingsContainer
        title="Organization management"
        description="Here's where you can edit and update your organization's information and display settings."
        bottomBar={bottomBarContent}
      >
        <form onSubmit={handleSubmit}>
          <FormSection title="Basic information">
            <FormInput
              name="name"
              onChange={onFormInputChange}
              placeholder="Organization name"
              label="Organization name"
              value={name}
              errors={errors}
            />

            <FormInput
              name="websiteUrl"
              onChange={onFormInputChange}
              placeholder="..."
              label="Organization website URL"
              description="If you supply a website URL, it will appear on your job board as a link."
              pretext="https://"
              value={removeHttpPrefix(websiteUrl) || ""}
              errors={errors}
            />

            <FormSelect
              onChange={onFormInputChange}
              name="remoteness"
              label="How remote is your organization?"
              description=""
              value={remoteness}
              options={expandedOrganizationRemotenessOptions}
              errors={errors}
              // isClearable
            />
          </FormSection>

          <FormSection title="Display settings">
            <FormSelect
              onChange={onFormInputChange}
              name="hudDisplayVisibility"
              label="Organization info display visibility"
              description="Choose who can see the job board subscriber count and published job count on navigation bar."
              value={hudDisplayVisibility}
              options={hudDisplayVisibilityOptions}
              errors={errors}
            />
          </FormSection>

          <FeatureFlipper feature="ORGANIZATION_DATA_EXPORTS">
            <FormSection
              title="Data exports"
              intro="Export your organization's core data for backup and external use. Select individual CSV exports or download everything together in a single ZIP file."
            >
              <div>
                <ButtonsSectionLabel>Individual CSV exports</ButtonsSectionLabel>
                <ButtonsContainer>
                  <Button
                    styleType="secondary"
                    onClick={onClickExportCandidates}
                    disabled={isCreatingDataExport}
                  >
                    Export candidates
                  </Button>
                  <Button
                    styleType="secondary"
                    onClick={onClickExportJobs}
                    disabled={isCreatingDataExport}
                  >
                    Export jobs
                  </Button>
                  <Button
                    styleType="secondary"
                    onClick={onClickExportJobApplications}
                    disabled={isCreatingDataExport}
                  >
                    Export job applications
                  </Button>
                </ButtonsContainer>
              </div>

              <div>
                <ButtonsSectionLabel>Bulk ZIP export</ButtonsSectionLabel>
                <ButtonsContainer>
                  <Button
                    styleType="secondary"
                    onClick={onClickExportComplete}
                    disabled={isCreatingDataExport}
                  >
                    Export all data
                  </Button>
                </ButtonsContainer>
              </div>
            </FormSection>
          </FeatureFlipper>
        </form>
      </SettingsContainer>
    </>
  );
}

export default AccountOrganizationDetails;

/* Styled Components
======================================================= */
const ButtonsContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountOrganizationDetails_ButtonsContainer;
    ${[t.mb(5)]}
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  `;
});

const ButtonsSectionLabel = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountOrganizationDetails_ButtonsSectionLabel;
    ${[t.text.sm, t.text.bold]}
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    display: inline-block;
    line-height: 1.4;
    margin-bottom: 0.375rem;

    ${t.mq["md"]} {
      ${[t.text.xs]}
    }
  `;
});
